/**
 * Thin fetch wrapper that sends credentials (cookies) and handles the
 * "401 → bounce to login" case in one place. Every page uses this.
 */

export async function api<T = unknown>(
  path: string,
  init: RequestInit = {}
): Promise<T> {
  const res = await fetch(path, {
    credentials: 'include',
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init.headers || {}),
    },
  });

  if (res.status === 401) {
    // Session missing or expired — send the user through the OIDC flow,
    // preserving where they were so we can bounce them back after login.
    const next = encodeURIComponent(window.location.pathname + window.location.search);
    window.location.href = `/auth/login?next=${next}`;
    throw new Error('Unauthorized');
  }

  if (res.status === 403) {
    // Hybrid team-switcher gate: requireUser middleware returns this
    // when the active team-context requires an IdP the current session
    // wasn't minted by. Body carries sso_start_url; send the browser
    // through that IdP. Same shape as the /api/team/switch 409.
    const body = (await res.clone().json().catch(() => null)) as
      | { error?: string; sso_start_url?: string }
      | null;
    if (body?.error === 'reauth_required' && body.sso_start_url) {
      window.location.href = body.sso_start_url;
      throw new Error('reauth_required');
    }
  }

  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`HTTP ${res.status}: ${body || res.statusText}`);
  }

  return res.json() as Promise<T>;
}
